/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useCallback, useEffect, useMemo, useState } from 'react'
import { marketingApex } from '@/lib/host'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  Check,
  ChevronDown,
  ExternalLink,
  KeyRound,
  LogOut,
  Settings,
  Wallet,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth-store'
import { api } from '@/lib/api'
import {
  getInterfaceLanguageOptions,
  normalizeInterfaceLanguage,
} from '@/i18n/languages'
import { useSystemConfigStore } from '@/stores/system-config-store'
import { getUserAvatarFallback, getUserAvatarStyle } from '@/lib/avatar'
import { cn } from '@/lib/utils'
import { useSystemConfig } from '@/hooks/use-system-config'
import { useTopNavLinks } from '@/hooks/use-top-nav-links'
import { useUserDisplay } from '@/hooks/use-user-display'
import { useFormatBalance } from '@/lib/use-format-balance'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { SignOutDialog } from '@/components/sign-out-dialog'
// ProfileDropdown is intentionally NOT imported here — the public header uses
// PublicHeaderUserButton (defined below) which adds a Console entry to the
// dropdown. The sidebar's ProfileDropdown remains unmodified.
import { defaultTopNavLinks } from '../config/top-nav.config'
import type { TopNavLink } from '../types'
import {
  AnnouncementBar,
  useAnnouncementBarNotice,
} from './announcement-bar'
import { HeaderLogo } from './header-logo'

// Short trigger label per language, mirroring the standalone homepage's
// `.navbar-lang` ("中" / "EN" / "日").
const LANG_SHORT: Record<string, string> = {
  zh: '中',
  tw: '繁',
  en: 'EN',
  ja: '日',
  ko: '한',
  fr: 'FR',
  ru: 'RU',
  vi: 'VI',
}

// ---------------------------------------------------------------------------
// PublicLanguageSwitcher — bordered pill + caret dropdown, styled to match the
// standalone homepage's `.navbar-lang` (not the icon-button used elsewhere).
// ---------------------------------------------------------------------------
function PublicLanguageSwitcher() {
  const { i18n, t } = useTranslation()
  const user = useAuthStore((state) => state.auth.user)
  const extraLangs = useSystemConfigStore((s) => s.config.extraInterfaceLangs)
  const languageOptions = getInterfaceLanguageOptions(extraLangs)
  const current = normalizeInterfaceLanguage(i18n.language)
  const shortLabel = LANG_SHORT[current] ?? current.slice(0, 2).toUpperCase()

  const handleChangeLanguage = useCallback(
    async (code: string) => {
      await i18n.changeLanguage(code)
      if (user) {
        try {
          await api.put('/api/user/self', { language: code })
        } catch {
          // Best-effort persistence; don't block the UI on failure.
        }
      }
    },
    [i18n, user]
  )

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        render={
          <button
            type='button'
            className='group border-border text-muted-foreground hover:border-primary/40 hover:text-foreground inline-flex h-8 items-center gap-1.5 rounded-[10px] border bg-transparent px-3 text-[13px] font-medium transition-colors duration-150'
            aria-label={t('Change language')}
          />
        }
      >
        <span>{shortLabel}</span>
        <ChevronDown className='size-3 opacity-60 transition-transform duration-200 group-aria-expanded:rotate-180' />
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' sideOffset={8} className='min-w-[150px]'>
        {languageOptions.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleChangeLanguage(lang.code)}
            className={cn(
              current === lang.code && 'text-primary bg-primary/10'
            )}
          >
            {lang.label}
            <Check
              className={cn('ms-auto size-3.5', current !== lang.code && 'hidden')}
            />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ---------------------------------------------------------------------------
// PublicHeaderUserButton — avatar pill + dropdown (logged-in) or Sign-in link
// (logged-out), styled to match the standalone homepage's `.navbar-user`.
// Scoped to the public header only so the sidebar's ProfileDropdown is
// unchanged.
// ---------------------------------------------------------------------------
function PublicHeaderUserButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [signOutOpen, setSignOutOpen] = useState(false)
  const user = useAuthStore((state) => state.auth.user)
  const { displayName, secondaryText } = useUserDisplay(user)
  const formatBalance = useFormatBalance()
  const avatarName = user?.username || displayName
  const avatarFallback = getUserAvatarFallback(avatarName)
  const avatarStyle = useMemo(() => getUserAvatarStyle(avatarName), [avatarName])
  const balance = formatBalance(user?.quota ?? 0)

  if (!user) {
    return (
      <Link
        to='/sign-in'
        className='inline-flex h-8 items-center rounded-[10px] border border-border bg-transparent px-4 text-[13px] font-medium text-muted-foreground transition-colors duration-150 hover:border-primary/50 hover:text-foreground'
      >
        {t('Sign in')}
      </Link>
    )
  }

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger
          render={
            <Button
              variant='ghost'
              className='group border-border bg-card hover:border-primary/40 hover:bg-card relative flex h-8 items-center gap-2 rounded-full border py-1 pr-3 pl-1 hover:shadow-sm'
              aria-label={t('User menu')}
            />
          }
        >
          {/* Circular avatar */}
          <Avatar className='size-[26px] shrink-0'>
            <AvatarFallback
              className='text-[11px] font-semibold text-white'
              style={avatarStyle}
            >
              {avatarFallback}
            </AvatarFallback>
          </Avatar>
          {/* Username label next to avatar */}
          <span className='max-w-[90px] truncate text-[13px] font-medium text-foreground'>
            {avatarName}
          </span>
          <ChevronDown className='size-3 opacity-60 transition-transform duration-200 group-aria-expanded:rotate-180' />
        </DropdownMenuTrigger>

        <DropdownMenuContent align='end' sideOffset={8} className='w-60 p-1.5'>
          {/* Header — avatar + name + secondary (email), matching the console's
              bottom-left user popover. No divider lines between rows. */}
          <div className='flex items-center gap-2.5 px-1.5 py-2'>
            <Avatar className='size-9 shrink-0'>
              <AvatarFallback
                className='text-xs font-semibold text-white'
                style={avatarStyle}
              >
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
            <div className='min-w-0 flex-1'>
              <p className='text-foreground truncate text-[13px] font-semibold'>
                {avatarName}
              </p>
              {secondaryText && (
                <p className='text-muted-foreground truncate text-[11px]'>
                  {secondaryText}
                </p>
              )}
            </div>
          </div>

          {/* Account balance */}
          <div className='flex items-center justify-between px-2.5 py-2 text-[13px]'>
            <span className='text-muted-foreground'>{t('Account Balance')}</span>
            <span className='text-foreground font-semibold'>{balance}</span>
          </div>

          <DropdownMenuItem onClick={() => navigate({ to: '/profile' })}>
            <Settings className='size-4' />
            {t('Account Settings')}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => navigate({ to: '/keys' })}>
            <KeyRound className='size-4' />
            {t('API Tokens')}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => navigate({ to: '/wallet' })}>
            <Wallet className='size-4' />
            {t('Top-up Center')}
          </DropdownMenuItem>

          <DropdownMenuItem
            variant='destructive'
            onClick={() => setSignOutOpen(true)}
          >
            <LogOut className='size-4' />
            {t('Log out')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SignOutDialog open={signOutOpen} onOpenChange={setSignOutOpen} />
    </>
  )
}

const AUTH_PROMPT_SECONDS = 5

type AuthPromptTarget = {
  title: string
  href: string
}

export interface PublicHeaderProps {
  navLinks?: TopNavLink[]
  mobileLinks?: TopNavLink[]
  navContent?: React.ReactNode
  showThemeSwitch?: boolean
  showLanguageSwitcher?: boolean
  logo?: React.ReactNode
  siteName?: string
  homeUrl?: string
  leftContent?: React.ReactNode
  rightContent?: React.ReactNode
  showNavigation?: boolean
  showAuthButtons?: boolean
  showNotifications?: boolean
  /**
   * 在菜单栏上方渲染系统公告横条(apiyi 式蓝色渐变条,见 announcement-bar.tsx)。
   * 仅营销主页开启;有公告时页头自动下移 40px。
   */
  showAnnouncementBar?: boolean
  className?: string
}

export function PublicHeader(props: PublicHeaderProps) {
  const {
    navLinks = defaultTopNavLinks,
    showLanguageSwitcher = true,
    logo: customLogo,
    siteName: customSiteName,
    showAuthButtons = true,
  } = props

  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  // Marketing main-site URL for the brand logo. Native <a> (not Link) so TanStack
  // router can't intercept the navigation — required to actually leave the SPA.
  const marketingLng = ((i18n.language || '').toLowerCase().startsWith('ja')
    ? 'ja'
    : (i18n.language || '').toLowerCase().startsWith('ko')
      ? 'ko'
      : (i18n.language || '').toLowerCase().startsWith('en')
        ? 'en'
        : 'zh')
  const marketingHomeUrl = `https://${marketingApex()}/?lang=${marketingLng}`
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [authPromptTarget, setAuthPromptTarget] =
    useState<AuthPromptTarget | null>(null)
  const [authPromptSecondsLeft, setAuthPromptSecondsLeft] =
    useState(AUTH_PROMPT_SECONDS)
  const { auth } = useAuthStore()
  const {
    systemName,
    logo: systemLogo,
    loading,
    logoLoaded,
  } = useSystemConfig()
  const dynamicLinks = useTopNavLinks()
  const routerState = useRouterState()
  const pathname = routerState.location.pathname
  const announcementText = useAnnouncementBarNotice(
    props.showAnnouncementBar === true
  )
  const hasAnnouncementBar = !!announcementText

  const user = auth.user
  const isAuthenticated = !!user
  const displaySiteName = customSiteName || systemName
  // Filter out the "About" nav link — "Contact Us" is surfaced as a dedicated
  // white-pill button in the right-side controls instead.
  const rawLinks = dynamicLinks.length > 0 ? dynamicLinks : navLinks
  const links = rawLinks.filter((l) => l.href !== '/about' && l.title !== t('About'))

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  useEffect(() => {
    if (!authPromptTarget) return

    const intervalId = window.setInterval(() => {
      setAuthPromptSecondsLeft((seconds) => Math.max(seconds - 1, 0))
    }, 1000)

    const timeoutId = window.setTimeout(() => {
      const redirect = authPromptTarget.href
      setAuthPromptTarget(null)
      navigate({ to: '/sign-in', search: { redirect } })
    }, AUTH_PROMPT_SECONDS * 1000)

    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [authPromptTarget, navigate])

  const closeAuthPrompt = useCallback(() => {
    setAuthPromptTarget(null)
    setAuthPromptSecondsLeft(AUTH_PROMPT_SECONDS)
  }, [])

  const navigateToSignIn = useCallback(() => {
    const redirect = authPromptTarget?.href || '/'
    setAuthPromptTarget(null)
    navigate({ to: '/sign-in', search: { redirect } })
  }, [authPromptTarget?.href, navigate])

  const handleNavLinkClick = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,
      link: TopNavLink,
      closeMobile = false
    ) => {
      if (link.disabled) {
        event.preventDefault()
        return
      }

      if (link.requiresAuth) {
        event.preventDefault()
        if (closeMobile) {
          setMobileOpen(false)
        }
        setAuthPromptSecondsLeft(AUTH_PROMPT_SECONDS)
        setAuthPromptTarget({
          title: t(link.title),
          href: link.href,
        })
        return
      }

      if (closeMobile) {
        setMobileOpen(false)
      }
    },
    [t]
  )

  return (
    <>
      {/* 系统公告横条 — 菜单栏上方,apiyi 式蓝色渐变;有内容才渲染 */}
      {hasAnnouncementBar && <AnnouncementBar text={announcementText} />}
      {/* Noya marketing topbar — flat full-width bar matching
          noyapi-homepage-styled.html .topbar: 54px tall, mint-white
          (var(--background)) translucent + blur, hairline bottom border,
          1200px-max inner row. (Replaces the previous floating "pill" header.) */}
      <header
        className={cn(
          'fixed inset-x-0 z-50 h-[54px] transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300',
          hasAnnouncementBar ? 'top-10' : 'top-0',
          // At the very top: transparent + no underline, so the topbar melts
          // into each page's hero gradient. Once scrolled (content under it):
          // solid translucent background + hairline underline + shadow return.
          scrolled
            ? 'border-b border-border/80 bg-background/95 backdrop-blur-xl supports-[backdrop-filter]:bg-background/85 shadow-[0_1px_12px_-6px_rgba(0,0,0,0.12)]'
            : 'border-b border-transparent bg-transparent'
        )}
      >
        <div className='mx-auto flex h-full max-w-[1200px] items-center px-5 md:px-10'>
          {/* Logo — native <a> so it actually leaves the SPA into 4allapi.com.
               TanStack's <Link> would be intercepted by client-side router.
               .topbar-logo: 30px rounded icon + 15px name, gap 9px. */}
          <a
            href={marketingHomeUrl}
            className='group mr-8 flex shrink-0 items-center gap-[9px]'
            aria-label={t('Go to home')}
          >
            <div className='flex size-[30px] shrink-0 items-center justify-center overflow-hidden rounded-lg transition-transform duration-300 group-hover:scale-105'>
              {loading ? (
                <Skeleton className='size-full rounded-lg' />
              ) : customLogo ? (
                customLogo
              ) : (
                <HeaderLogo
                  src={systemLogo}
                  loading={loading}
                  logoLoaded={logoLoaded}
                  className='size-full rounded-lg object-contain'
                />
              )}
            </div>
            <span className='text-[17px] font-bold tracking-tight' style={{ color: '#1e3a8a' }}>
              {loading ? <Skeleton className='h-4 w-16' /> : displaySiteName}
            </span>
          </a>

          {/* Desktop nav — .topbar-nav: flex-1, gap 2px, .nav-link 13px,
               text-2 → hover bg accent + text-1, active = primary. */}
          <nav className='hidden flex-1 items-center gap-0.5 sm:flex'>
            {links.map((link, i) => {
              const isActive = pathname === link.href
              if (link.external) {
                return (
                  <a
                    key={i}
                    href={link.href}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-disabled={link.disabled}
                    tabIndex={link.disabled ? -1 : undefined}
                    onClick={(event) => handleNavLinkClick(event, link)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-[10px] px-3 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground',
                      link.disabled && 'pointer-events-none opacity-50'
                    )}
                  >
                    {t(link.title)}
                    <ExternalLink className='size-3 opacity-60' />
                  </a>
                )
              }
              return (
                <Link
                  key={i}
                  to={link.href}
                  disabled={link.disabled}
                  onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                    handleNavLinkClick(event, link)}
                  className={cn(
                    'rounded-[10px] px-3 py-1.5 text-[13px] font-medium transition-colors duration-150',
                    isActive
                      ? 'text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    link.disabled && 'pointer-events-none opacity-50'
                  )}
                >
                  {t(link.title)}
                </Link>
              )
            })}
          </nav>

          {/* Right-side actions — .topbar-right: gap 8px, ghost + teal pill. */}
          <div className='ml-auto hidden shrink-0 items-center gap-2 sm:flex'>
            {/* Contact Us — white-bg pill, placed before the language switcher.
                Opens the support/community page in a new tab (same target as
                the footer's "联系我们" link). */}
            {/* Contact Us — opens the bottom-right 智能客服 chat widget.
                The widget (chat-widget-v4.js, loaded in index.html) listens for
                clicks on any [data-contact-open] element (document-level capture
                delegation) and opens its panel. */}
            <button
              type='button'
              data-contact-open
              className='inline-flex h-8 items-center rounded-full border border-border bg-card px-4 text-[13px] font-medium text-foreground transition-colors duration-150 hover:bg-accent'
              aria-label={t('Contact Us')}
            >
              {t('Contact Us')}
            </button>
            {showLanguageSwitcher && <PublicLanguageSwitcher />}

            {showAuthButtons && (
              <>
                {loading ? (
                  <Skeleton className='h-8 w-24 rounded-full' />
                ) : isAuthenticated ? (
                  <PublicHeaderUserButton />
                ) : (
                  /* Logged-out: Sign in ghost + Get started pill */
                  <div className='flex items-center gap-2'>
                    <PublicHeaderUserButton />
                    <Link
                      to='/sign-up'
                      className='inline-flex h-8 items-center rounded-full bg-primary px-[18px] text-[13px] font-medium text-primary-foreground transition-all duration-150 hover:-translate-y-px hover:bg-[oklch(0.62_0.11_184)]'
                      style={{
                        boxShadow: '0 2px 10px oklch(0.704 0.124 182.5 / 25%)',
                      }}
                    >
                      {t('Get started free')}
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mobile: compact actions + hamburger */}
          <div className='ml-auto flex items-center gap-2 sm:hidden'>
              {showAuthButtons && !loading && (
                <PublicHeaderUserButton />
              )}
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-9'
                onClick={() => setMobileOpen((v) => !v)}
                aria-label={t('Toggle navigation menu')}
              >
                <div className='relative size-4'>
                  <span
                    className={cn(
                      'absolute inset-x-0 block h-[1.5px] origin-center rounded-full bg-current transition-all duration-300',
                      mobileOpen ? 'top-[7px] rotate-45' : 'top-[3px]'
                    )}
                  />
                  <span
                    className={cn(
                      'absolute inset-x-0 top-[7px] block h-[1.5px] rounded-full bg-current transition-all duration-300',
                      mobileOpen ? 'scale-x-0 opacity-0' : 'opacity-100'
                    )}
                  />
                  <span
                    className={cn(
                      'absolute inset-x-0 block h-[1.5px] origin-center rounded-full bg-current transition-all duration-300',
                      mobileOpen ? 'top-[7px] -rotate-45' : 'top-[11px]'
                    )}
                  />
                </div>
              </Button>
            </div>
        </div>
      </header>

      {/* Mobile full-screen overlay */}
      <div
        className={cn(
          'bg-background/98 fixed inset-0 z-40 backdrop-blur-2xl transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] sm:pointer-events-none sm:hidden',
          mobileOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'
        )}
      >
        <div className='flex h-full flex-col justify-between px-8 pt-20 pb-10'>
          <nav className='flex flex-col gap-1'>
            {links.map((link, i) => {
              const isActive = pathname === link.href
              const linkClassName = cn(
                'flex items-center gap-3 py-3 text-base font-medium tracking-tight transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
                mobileOpen
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-4 opacity-0',
                isActive ? 'text-foreground' : 'text-muted-foreground',
                link.disabled && 'pointer-events-none opacity-50'
              )
              const transitionStyle = {
                transitionDelay: mobileOpen ? `${100 + i * 50}ms` : '0ms',
              }
              if (link.external) {
                return (
                  <a
                    key={i}
                    href={link.href}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-disabled={link.disabled}
                    tabIndex={link.disabled ? -1 : undefined}
                    onClick={(event) => handleNavLinkClick(event, link, true)}
                    className={linkClassName}
                    style={transitionStyle}
                  >
                    {t(link.title)}
                    <ExternalLink className='size-4 opacity-60' />
                  </a>
                )
              }
              return (
                <Link
                  key={i}
                  to={link.href}
                  disabled={link.disabled}
                  onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                    handleNavLinkClick(event, link, true)}
                  className={linkClassName}
                  style={transitionStyle}
                >
                  {t(link.title)}
                </Link>
              )
            })}
          </nav>

          {/* Mobile bottom CTA — only shown when logged out */}
          {showAuthButtons && !isAuthenticated && (
            <div
              className={cn(
                'flex flex-col gap-3 transition-all duration-500',
                mobileOpen
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-4 opacity-0'
              )}
              style={{ transitionDelay: mobileOpen ? '250ms' : '0ms' }}
            >
              <Link
                to='/sign-in'
                onClick={() => setMobileOpen(false)}
                className='border-border text-foreground inline-flex h-10 items-center justify-center rounded-lg border text-sm font-medium transition-colors hover:bg-muted/50'
              >
                {t('Sign in')}
              </Link>
              <Link
                to='/sign-up'
                onClick={() => setMobileOpen(false)}
                className='inline-flex h-10 items-center justify-center rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90 active:opacity-80'
                style={{ background: 'oklch(0.704 0.124 182.5)' }}
              >
                {t('Get started free')}
              </Link>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={!!authPromptTarget}
        onOpenChange={(open) => {
          if (!open) {
            closeAuthPrompt()
          }
        }}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('Sign in required')}</DialogTitle>
            <DialogDescription>
              {t('Please sign in to view {{module}}.', {
                module: authPromptTarget?.title || '',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className='bg-muted/40 text-muted-foreground rounded-lg px-3 py-2 text-sm'>
            {t('Redirecting to sign in in {{seconds}} seconds.', {
              seconds: authPromptSecondsLeft,
            })}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={closeAuthPrompt}>
              {t('Cancel')}
            </Button>
            <Button onClick={navigateToSignIn}>{t('Sign in now')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
